import React, { useState, useEffect } from 'react'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog'
import { Button } from '../ui/button'
import { Progress } from '../ui/progress'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import { companyAPI } from '../../services/api'
import { toast } from '../../hooks/use-toast'
import { useNavigate } from 'react-router-dom'
import {
  Upload,
  FileText,
  AlertCircle,
  Loader2,
  Crop,
  ExternalLink,
  Info,
  Lock,
  HelpCircle
} from 'lucide-react'
import ImageCropModal from '../common/ImageCropModal'
import { validateEmail, validatePhone, validateLinkedInURL, validateRequired, normalizeURL } from '../../utils/validation'
import CountryCodeSelect from '../common/CountryCodeSelect'
import { getPOCData, validatePresenterVsPOC } from '../../utils/pocStorage'

export default function PitchDeckFeedbackModal({ open, onClose }) {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [fetchingData, setFetchingData] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [existingFeedback, setExistingFeedback] = useState(null)

  // Check if form is locked (already submitted)
  // Only lock if presenter-specific fields are complete, not just pitch deck URLs
  const isFormLocked = existingFeedback?.presenterFirstName &&
                       existingFeedback?.presenterLastName &&
                       existingFeedback?.presenterEmail &&
                       existingFeedback?.presenterLinkedIn

  // Presenter information states
  const [presenterData, setPresenterData] = useState({
    presenterFirstName: '',
    presenterLastName: '',
    presenterTitle: '',
    presenterCountryCode: '+91',
    presenterMobile: '',
    presenterEmail: '',
    presenterLinkedIn: '',
    presenterCompanyName: '',
    presenterPhoto: null
  })

  // Validation states
  const [errors, setErrors] = useState({})

  // POC data and toggle states
  const [pocData, setPocData] = useState(null)
  const [companyName, setCompanyName] = useState('')
  const [fillWithPOC, setFillWithPOC] = useState(false)
  const [pocValidationErrors, setPocValidationErrors] = useState([])
  const [hasMixedPOCData, setHasMixedPOCData] = useState(false)
  const [fieldPOCMatches, setFieldPOCMatches] = useState({
    name: false,
    email: false,
    phone: false
  })
  const [showInlineErrors, setShowInlineErrors] = useState(false) // Only show after blur or submit attempt
  const [showMixedDataConfirmation, setShowMixedDataConfirmation] = useState(false)
  const [mixedDataConflicts, setMixedDataConflicts] = useState([]) // Store which fields conflict

  // Cropping states
  const [cropModalOpen, setCropModalOpen] = useState(false)
  const [tempImageSrc, setTempImageSrc] = useState(null)
  const [croppedImageUrl, setCroppedImageUrl] = useState(null)
  const [existingPhotoUrl, setExistingPhotoUrl] = useState(null)

  // Check if all required fields are filled AND valid
  const areRequiredFieldsFilled = () => {
    const hasPhoto = existingPhotoUrl || presenterData.presenterPhoto || croppedImageUrl

    // Check if all fields are filled
    const allFieldsFilled = (
      presenterData.presenterFirstName.trim() !== '' &&
      presenterData.presenterLastName.trim() !== '' &&
      presenterData.presenterCompanyName.trim() !== '' &&
      presenterData.presenterTitle.trim() !== '' &&
      presenterData.presenterCountryCode.trim() !== '' &&
      presenterData.presenterMobile.trim() !== '' &&
      presenterData.presenterEmail.trim() !== '' &&
      presenterData.presenterLinkedIn.trim() !== '' &&
      hasPhoto
    )

    // Check if there are no validation errors (check for truthy values, not just keys)
    const hasNoErrors = Object.values(errors).every(error => !error)

    return allFieldsFilled && hasNoErrors
  }

  // Fetch existing feedback when modal opens
  useEffect(() => {
    if (open) {
      fetchExistingFeedback()
    } else {
      // Reset state when modal closes
      resetForm()
    }
  }, [open])

  // Real-time validation: Check for mixed POC data whenever presenter data changes
  useEffect(() => {
    if (!pocData || !open) {
      setHasMixedPOCData(false)
      setPocValidationErrors([])
      setFieldPOCMatches({ name: false, email: false, phone: false })
      return
    }

    // Only validate if at least some fields are filled
    const hasAnyData = presenterData.presenterFirstName ||
                       presenterData.presenterLastName ||
                       presenterData.presenterEmail ||
                       presenterData.presenterMobile

    if (!hasAnyData) {
      setHasMixedPOCData(false)
      setPocValidationErrors([])
      setFieldPOCMatches({ name: false, email: false, phone: false })
      return
    }

    // Run validation
    const pocValidation = validatePresenterVsPOC(presenterData, pocData)

    if (!pocValidation.isValid) {
      // Mixed data detected
      setHasMixedPOCData(true)
      setPocValidationErrors(pocValidation.errors)

      // Determine which specific fields match
      const normalize = (str) => (str || '').trim().toLowerCase()
      const normalizePhone = (phone) => (phone || '').replace(/\D/g, '')

      const presenterFullName = normalize(`${presenterData.presenterFirstName} ${presenterData.presenterLastName}`)
      const pocFullName = normalize(`${pocData.pocFirstName} ${pocData.pocLastName}`)
      const nameMatches = presenterFullName && pocFullName && presenterFullName === pocFullName

      const presenterEmail = normalize(presenterData.presenterEmail)
      const pocEmail = normalize(pocData.pocEmail)
      const emailMatches = presenterEmail && pocEmail && presenterEmail === pocEmail

      const presenterPhone = normalizePhone(presenterData.presenterMobile)
      const pocPhone = normalizePhone(pocData.pocPhone)
      const phoneMatches = presenterPhone && pocPhone && presenterPhone === pocPhone

      setFieldPOCMatches({
        name: nameMatches,
        email: emailMatches,
        phone: phoneMatches
      })
    } else {
      // No mixed data - either all match or all different
      setHasMixedPOCData(false)
      setPocValidationErrors([])
      setFieldPOCMatches({ name: false, email: false, phone: false })
    }
  }, [presenterData.presenterFirstName, presenterData.presenterLastName, presenterData.presenterEmail, presenterData.presenterMobile, pocData, open])

  const fetchExistingFeedback = async () => {
    setFetchingData(true)
    try {
      const response = await companyAPI.getPitchDeckFeedback()
      if (response.data.success) {
        // Get company name from response
        const fetchedCompanyName = response.data.companyName || ''
        setCompanyName(fetchedCompanyName)

        // Load POC data from API response or sessionStorage
        const pocDataFromAPI = response.data.pocData
        const pocDataFromStorage = getPOCData()
        const loadedPocData = pocDataFromAPI || pocDataFromStorage

        if (loadedPocData) {
          setPocData(loadedPocData)
        }

        if (response.data.feedback) {
          setExistingFeedback(response.data.feedback)

          // Pre-populate presenter data if it exists
          const countryCode = response.data.feedback.presenterCountryCode || '91'
          // Add + if not present
          const formattedCountryCode = countryCode.startsWith('+') ? countryCode : `+${countryCode}`

          setPresenterData({
            presenterFirstName: response.data.feedback.presenterFirstName || '',
            presenterLastName: response.data.feedback.presenterLastName || '',
            presenterTitle: response.data.feedback.presenterTitle || '',
            presenterCountryCode: formattedCountryCode,
            presenterMobile: response.data.feedback.presenterMobile || '',
            presenterEmail: response.data.feedback.presenterEmail || '',
            presenterLinkedIn: response.data.feedback.presenterLinkedIn || '',
            presenterCompanyName: response.data.feedback.presenterCompanyName || fetchedCompanyName, // Use saved value or default to company name
            presenterPhoto: null // File input can't be pre-populated
          })

          // Set existing photo URL if available
          if (response.data.feedback.presenterPhotoPath) {
            setExistingPhotoUrl(response.data.feedback.presenterPhotoPath)
          }
        } else {
          // No existing feedback, just set the company name
          setPresenterData(prevData => ({
            ...prevData,
            presenterCompanyName: fetchedCompanyName
          }))
        }
      }
    } catch (error) {
      console.log('No existing pitch deck feedback found')
    } finally {
      setFetchingData(false)
    }
  }

  // Handle "Fill with POC" toggle
  const handleFillWithPOC = (checked) => {
    setFillWithPOC(checked)

    if (checked && pocData) {
      // Auto-fill presenter data with POC information
      setPresenterData(prevData => ({
        ...prevData,
        presenterFirstName: pocData.pocFirstName || '',
        presenterLastName: pocData.pocLastName || '',
        presenterCountryCode: pocData.pocCountryCode || '+91',
        presenterMobile: pocData.pocPhone || '',
        presenterEmail: pocData.pocEmail || '',
        presenterCompanyName: prevData.presenterCompanyName || companyName, // Keep existing or use fetched company name
        // Keep title, LinkedIn, and photo unchanged (user needs to fill these)
      }))

      // Clear any validation errors for locked fields
      setErrors(prevErrors => {
        const { presenterFirstName, presenterLastName, presenterEmail, presenterMobile, presenterCompanyName, ...rest } = prevErrors
        return rest
      })
    } else if (!checked) {
      // When unchecked, clear only the POC-specific auto-filled fields
      setPresenterData(prevData => ({
        ...prevData,
        presenterFirstName: '',
        presenterLastName: '',
        presenterCountryCode: '+91',
        presenterMobile: '',
        presenterEmail: '',
        // Keep company name, title, LinkedIn, and photo unchanged
      }))

      // Clear any validation errors for the cleared fields
      setErrors(prevErrors => {
        const { presenterFirstName, presenterLastName, presenterEmail, presenterMobile, ...rest } = prevErrors
        return rest
      })
    }
  }

  const resetForm = () => {
    setPresenterData({
      presenterFirstName: '',
      presenterLastName: '',
      presenterTitle: '',
      presenterCountryCode: '+91',
      presenterMobile: '',
      presenterEmail: '',
      presenterLinkedIn: '',
      presenterCompanyName: '',
      presenterPhoto: null
    })
    setErrors({})
    setPocValidationErrors([])
    setFillWithPOC(false)
    setShowInlineErrors(false) // Reset inline error visibility
    setUploadProgress(0)
    setCroppedImageUrl(null)
    setTempImageSrc(null)
    setExistingPhotoUrl(null)
  }

  const handleImageSelect = (e) => {
    const file = e.target.files[0]
    if (file) {
      // Validate file size (100KB min)
      const minSize = 100 * 1024 // 100KB in bytes
      if (file.size < minSize) {
        toast.error('File size must be at least 100KB. Please choose a higher resolution image.')
        e.target.value = '' // Clear the input
        return
      }

      const reader = new FileReader()
      reader.onload = () => {
        setTempImageSrc(reader.result)
        setCropModalOpen(true)
      }
      reader.readAsDataURL(file)
    }
  }

  const handleCropComplete = (croppedImage) => {
    setPresenterData({ ...presenterData, presenterPhoto: croppedImage.file })
    setCroppedImageUrl(croppedImage.url)
    setCropModalOpen(false)
    setTempImageSrc(null)
  }

  // Handle navigate to help page
  const handleGoToHelp = () => {
    onClose() // Close the modal first
    navigate('/company/help')
  }

  // Handle confirmation dialog actions
  const handleConfirmYes = () => {
    // User confirms they are the presenter - enable the toggle
    setShowMixedDataConfirmation(false)
    setFillWithPOC(true)

    // Auto-fill with POC data
    if (pocData) {
      setPresenterData(prevData => ({
        ...prevData,
        presenterFirstName: pocData.pocFirstName || '',
        presenterLastName: pocData.pocLastName || '',
        presenterCountryCode: pocData.pocCountryCode || '+91',
        presenterMobile: pocData.pocPhone || '',
        presenterEmail: pocData.pocEmail || '',
        presenterCompanyName: prevData.presenterCompanyName || companyName,
      }))

      // Clear errors
      setErrors(prevErrors => {
        const { presenterFirstName, presenterLastName, presenterEmail, presenterMobile, presenterCompanyName, ...rest } = prevErrors
        return rest
      })
    }
  }

  const handleConfirmEdit = () => {
    // Clear the conflicting fields (both matching and non-matching)
    const clearedData = { ...presenterData }
    const allConflicts = [...(mixedDataConflicts.matching || []), ...(mixedDataConflicts.nonMatching || [])]

    if (allConflicts.includes('name')) {
      clearedData.presenterFirstName = ''
      clearedData.presenterLastName = ''
    }
    if (allConflicts.includes('email')) {
      clearedData.presenterEmail = ''
    }
    if (allConflicts.includes('phone')) {
      clearedData.presenterMobile = ''
      clearedData.presenterCountryCode = '+91'
    }

    setPresenterData(clearedData)
    setShowMixedDataConfirmation(false)

    // Clear errors for these fields
    setErrors(prevErrors => {
      const newErrors = { ...prevErrors }
      if (allConflicts.includes('name')) {
        delete newErrors.presenterFirstName
        delete newErrors.presenterLastName
      }
      if (allConflicts.includes('email')) delete newErrors.presenterEmail
      if (allConflicts.includes('phone')) delete newErrors.presenterMobile
      return newErrors
    })
  }



  const handleSubmit = async () => {
    const validationErrors = {}

    // Validate required fields
    const firstNameValidation = validateRequired(presenterData.presenterFirstName, 'First name')
    if (!firstNameValidation.isValid) validationErrors.presenterFirstName = firstNameValidation.error

    const lastNameValidation = validateRequired(presenterData.presenterLastName, 'Last name')
    if (!lastNameValidation.isValid) validationErrors.presenterLastName = lastNameValidation.error

    const companyNameValidation = validateRequired(presenterData.presenterCompanyName, 'Company name')
    if (!companyNameValidation.isValid) validationErrors.presenterCompanyName = companyNameValidation.error

    const titleValidation = validateRequired(presenterData.presenterTitle, 'Designation')
    if (!titleValidation.isValid) validationErrors.presenterTitle = titleValidation.error

    // Validate email
    const emailValidation = validateEmail(presenterData.presenterEmail)
    if (!emailValidation.isValid) validationErrors.presenterEmail = emailValidation.error

    // Validate phone
    const phoneValidation = validatePhone(presenterData.presenterMobile)
    if (!phoneValidation.isValid) validationErrors.presenterMobile = phoneValidation.error

    // Validate LinkedIn URL
    const linkedInValidation = validateLinkedInURL(presenterData.presenterLinkedIn)
    if (!linkedInValidation.isValid) validationErrors.presenterLinkedIn = linkedInValidation.error

    // Validate presenter photo is mandatory if not already uploaded
    if (!existingPhotoUrl && !presenterData.presenterPhoto && !croppedImageUrl) {
      validationErrors.presenterPhoto = 'Presenter photo is required'
    }

    // If there are any validation errors, show them and stop submission
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors)
      // toast.error('Please fix all validation errors before submitting')
      return
    }

    // CRITICAL: Validate presenter data doesn't have mixed POC/non-POC data
    if (pocData) {
      const pocValidation = validatePresenterVsPOC(presenterData, pocData)
      if (!pocValidation.isValid) {
        // Instead of blocking, show confirmation dialog
        setPocValidationErrors(pocValidation.errors)

        // Determine which fields match and which don't
        const matchingFields = []
        const nonMatchingFields = []

        if (fieldPOCMatches.name) {
          matchingFields.push('name')
        } else {
          nonMatchingFields.push('name')
        }

        if (fieldPOCMatches.email) {
          matchingFields.push('email')
        } else {
          nonMatchingFields.push('email')
        }

        if (fieldPOCMatches.phone) {
          matchingFields.push('phone')
        } else {
          nonMatchingFields.push('phone')
        }

        setMixedDataConflicts({ matching: matchingFields, nonMatching: nonMatchingFields })
        setShowMixedDataConfirmation(true)
        return
      }
    }

    // Clear any previous errors
    setErrors({})
    setPocValidationErrors([])
    setShowInlineErrors(false)

    setLoading(true)
    setUploadProgress(0)

    try {
      const formData = new FormData()

      // Append presenter information
      formData.append('presenterFirstName', presenterData.presenterFirstName)
      formData.append('presenterLastName', presenterData.presenterLastName)
      formData.append('presenterTitle', presenterData.presenterTitle)
      // Remove + from country code before saving
      formData.append('presenterCountryCode', presenterData.presenterCountryCode.replace('+', ''))
      formData.append('presenterMobile', presenterData.presenterMobile)
      formData.append('presenterEmail', presenterData.presenterEmail)
      formData.append('presenterLinkedIn', presenterData.presenterLinkedIn)
      formData.append('presenterCompanyName', presenterData.presenterCompanyName)

      // Preserve existing URLs if they exist
      if (existingFeedback) {
        formData.append('documentUrl', existingFeedback.documentUrl || '')
        formData.append('videoUrl', existingFeedback.videoUrl || '')
      } else {
        formData.append('documentUrl', '')
        formData.append('videoUrl', '')
      }

      if (presenterData.presenterPhoto) {
        formData.append('presenterPhoto', presenterData.presenterPhoto)
      }

      // Track upload progress
      const config = {
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total)
          setUploadProgress(percentCompleted)
        }
      }

      const response = existingFeedback
        ? await companyAPI.updatePitchDeckFeedback(formData, config)
        : await companyAPI.submitPitchDeckFeedback(formData, config)

      if (response.data.success) {
        toast.success('Presenter information submitted successfully!')
        resetForm()
        onClose()
      }
    } catch (error) {
      console.error('Error submitting pitch deck feedback:', error)
      setErrors({ general: 'Failed to submit feedback. Please try again.' })
    } finally {
      setLoading(false)
      setUploadProgress(0)
    }
  }


  return (
    <>
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="max-w-3xl max-h-[90vh] !flex !flex-col !overflow-hidden !p-0 [&>button]:top-6 [&>button]:right-6">
          <DialogHeader className="flex-shrink-0 p-6 pb-0 border-b">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <FileText className="h-6 w-6 text-purple-600" />
              </div>
              <div>
                <DialogTitle className="text-xl">Presenter Information</DialogTitle>
                <DialogDescription className="mt-1 pb-4">
                  Provide information about the person who will present at IFTA 2025
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>

          {fetchingData ? (
            <div className="flex-1 flex items-center justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-purple-600" />
            </div>
          ) : (
            <div className="flex-1 overflow-y-auto p-6 space-y-6">
            {/* Locked Form Banner */}
            {isFormLocked && (
              <div className="flex items-start gap-3 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <Lock className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm text-blue-800">
                    Your presenter information has been submitted. If you need to make any changes, please contact our support team for assistance.
                  </p>
                </div>
                <Button
                  onClick={handleGoToHelp}
                  variant="outline"
                  size="sm"
                  className="flex-shrink-0 border-blue-300 text-blue-700 hover:bg-blue-100"
                >
                  <HelpCircle className="h-4 w-4 mr-1" />
                  Help
                </Button>
              </div>
            )}

            {/* Presenter Information Section */}
            <div className="space-y-4">
              <div className="border-b pb-2">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-primary">Presenter Information</h3>

                  {/* "Fill with POC" Toggle - Hidden when form is locked */}
                  {pocData && !isFormLocked && (
                    <div className="flex items-center gap-2">
                      <Label htmlFor="fillWithPOC" className="text-sm text-gray-600 cursor-pointer">
                        I am the Presenter
                      </Label>
                      <input
                        id="fillWithPOC"
                        type="checkbox"
                        checked={fillWithPOC}
                        onChange={(e) => handleFillWithPOC(e.target.checked)}
                        className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary cursor-pointer"
                      />
                    </div>
                  )}
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="presenterFirstName" className="text-sm font-medium">
                    First Name * {fillWithPOC && <span className="text-xs text-gray-500">(Locked)</span>}
                  </Label>
                  <Input
                    id="presenterFirstName"
                    placeholder="First name"
                    value={presenterData.presenterFirstName}
                    onChange={(e) => {
                      setPresenterData({ ...presenterData, presenterFirstName: e.target.value })
                    }}
                    disabled={isFormLocked || fillWithPOC}
                    className={`h-10 ${isFormLocked || fillWithPOC ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="presenterLastName" className="text-sm font-medium">
                    Last Name * {fillWithPOC && <span className="text-xs text-gray-500">(Locked)</span>}
                  </Label>
                  <Input
                    id="presenterLastName"
                    placeholder="Last name"
                    value={presenterData.presenterLastName}
                    onChange={(e) => {
                      setPresenterData({ ...presenterData, presenterLastName: e.target.value })
                    }}
                    disabled={isFormLocked || fillWithPOC}
                    className={`h-10 ${isFormLocked || fillWithPOC ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="presenterCompanyName" className="text-sm font-medium">
                    Company Name * {fillWithPOC && <span className="text-xs text-gray-500">(Locked)</span>}
                  </Label>
                  <Input
                    id="presenterCompanyName"
                    placeholder="Company name"
                    value={presenterData.presenterCompanyName}
                    onChange={(e) => setPresenterData({ ...presenterData, presenterCompanyName: e.target.value })}
                    disabled={isFormLocked || fillWithPOC}
                    className={`h-10 ${isFormLocked || fillWithPOC ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="presenterTitle" className="text-sm font-medium">
                    Designation *
                  </Label>
                  <Input
                    id="presenterTitle"
                    placeholder="e.g., CEO, CTO, Product Manager"
                    value={presenterData.presenterTitle}
                    onChange={(e) => setPresenterData({ ...presenterData, presenterTitle: e.target.value })}
                    disabled={isFormLocked}
                    className={`h-10 ${isFormLocked ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium">
                    Country Code * {fillWithPOC && <span className="text-xs text-gray-500">(Locked)</span>}
                  </Label>
                  <CountryCodeSelect
                    value={presenterData.presenterCountryCode}
                    onChange={(code) => setPresenterData({ ...presenterData, presenterCountryCode: code })}
                    disabled={isFormLocked || fillWithPOC}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="presenterMobile" className="text-sm font-medium">
                    Mobile Number * {fillWithPOC && <span className="text-xs text-gray-500">(Locked)</span>}
                  </Label>
                  <Input
                    id="presenterMobile"
                    type="tel"
                    placeholder="1234567890"
                    value={presenterData.presenterMobile}
                    onChange={(e) => {
                      setPresenterData({ ...presenterData, presenterMobile: e.target.value })
                      // Clear error when user types
                      if (errors.presenterMobile) {
                        setErrors({ ...errors, presenterMobile: '' })
                      }
                    }}
                    onBlur={(e) => {
                      // Only validate on blur if not locked
                      if (!fillWithPOC && !isFormLocked) {
                        const validation = validatePhone(e.target.value)
                        if (!validation.isValid) {
                          setErrors({ ...errors, presenterMobile: validation.error })
                        }
                      }
                    }}
                    disabled={isFormLocked || fillWithPOC}
                    className={`h-10 ${isFormLocked || fillWithPOC ? 'bg-gray-100 cursor-not-allowed' : errors.presenterMobile ? 'border-red-500 bg-red-50' : ''}`}
                  />
                  {!fillWithPOC && !isFormLocked && errors.presenterMobile && (
                    <p className="text-sm text-red-500">{errors.presenterMobile}</p>
                  )}
                  <p className="text-xs text-muted-foreground">
                    Enter mobile number (7-15 digits)
                  </p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="presenterEmail" className="text-sm font-medium">
                    Email * {fillWithPOC && <span className="text-xs text-gray-500">(Locked)</span>}
                  </Label>
                  <Input
                    id="presenterEmail"
                    type="email"
                    placeholder="presenter@company.com"
                    value={presenterData.presenterEmail}
                    onChange={(e) => {
                      setPresenterData({ ...presenterData, presenterEmail: e.target.value })
                      // Clear error when user types
                      if (errors.presenterEmail) {
                        setErrors({ ...errors, presenterEmail: '' })
                      }
                    }}
                    onBlur={(e) => {
                      // Only validate on blur if not locked
                      if (!fillWithPOC && !isFormLocked) {
                        const validation = validateEmail(e.target.value)
                        if (!validation.isValid) {
                          setErrors({ ...errors, presenterEmail: validation.error })
                        }
                      }
                    }}
                    disabled={isFormLocked || fillWithPOC}
                    className={`h-10 ${isFormLocked || fillWithPOC ? 'bg-gray-100 cursor-not-allowed' : errors.presenterEmail ? 'border-red-500 bg-red-50' : ''}`}
                  />
                  {!fillWithPOC && !isFormLocked && errors.presenterEmail && (
                    <p className="text-sm text-red-500">{errors.presenterEmail}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="presenterLinkedIn" className="text-sm font-medium">
                    LinkedIn Profile <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="presenterLinkedIn"
                    type="url"
                    placeholder="https://www.linkedin.com/in/presenter"
                    value={presenterData.presenterLinkedIn}
                    onChange={(e) => {
                      setPresenterData({ ...presenterData, presenterLinkedIn: e.target.value })
                      // Clear error when user types
                      if (errors.presenterLinkedIn) {
                        setErrors({ ...errors, presenterLinkedIn: '' })
                      }
                    }}
                    onBlur={(e) => {
                      // Normalize URL (add https:// if missing) and validate
                      if (!isFormLocked) {
                        const normalizedURL = normalizeURL(e.target.value)
                        if (normalizedURL) {
                          setPresenterData({ ...presenterData, presenterLinkedIn: normalizedURL })
                          const validation = validateLinkedInURL(normalizedURL)
                          if (!validation.isValid) {
                            setErrors({ ...errors, presenterLinkedIn: validation.error })
                          }
                        }
                      }
                    }}
                    disabled={isFormLocked}
                    className={`h-10 ${isFormLocked ? 'bg-gray-100 cursor-not-allowed' : errors.presenterLinkedIn ? 'border-red-500' : ''}`}
                    required
                  />
                  {!isFormLocked && errors.presenterLinkedIn && (
                    <p className="text-sm text-red-500">{errors.presenterLinkedIn}</p>
                  )}
                  <p className="text-xs text-muted-foreground">
                    Example: https://www.linkedin.com/in/yourprofile
                  </p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="presenterPhoto" className="text-sm font-medium">
                    Presenter Photo *
                  </Label>
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-3">
                    <p className="text-xs text-amber-800">
                      <strong>Note:</strong> Upload a high-resolution professional headshot (SVG preferred) for print and digital use.
                    </p>
                  </div>
                  <div className={`border-2 border-dashed rounded-lg p-4 ${isFormLocked ? 'border-gray-200 bg-gray-50' : 'border-gray-300 bg-gray-50'}`}>
                    {croppedImageUrl || existingPhotoUrl || presenterData.presenterPhoto ? (
                      <div className="space-y-3">
                        <div className="flex justify-center">
                          <img
                            src={croppedImageUrl || existingPhotoUrl || (presenterData.presenterPhoto ? URL.createObjectURL(presenterData.presenterPhoto) : '')}
                            alt="Presenter"
                            className="w-32 h-32 object-cover rounded-lg shadow-md"
                          />
                        </div>
                        <div className="text-center">
                          <p className="text-sm font-medium text-gray-700">
                            {presenterData.presenterPhoto?.name || (existingPhotoUrl ? 'Existing Presenter Photo' : 'Cropped Image')}
                          </p>
                          <p className="text-xs text-gray-500">
                            Photo (Cropped)
                          </p>
                        </div>
                        {/* Hide Change & Remove buttons when form is locked */}
                        {!isFormLocked && (
                          <div className="flex gap-2 justify-center">
                            <Label htmlFor="presenterPhotoChange" className="cursor-pointer">
                              <Input
                                id="presenterPhotoChange"
                                type="file"
                                accept="image/*"
                                onChange={handleImageSelect}
                                className="hidden"
                              />
                              <span className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 cursor-pointer">
                                <Crop className="h-3 w-3 mr-1" />
                                Change & Crop
                              </span>
                            </Label>
                            <button
                              type="button"
                              onClick={() => {
                                setPresenterData({ ...presenterData, presenterPhoto: null })
                                setCroppedImageUrl(null)
                                setExistingPhotoUrl(null)
                              }}
                              className="inline-flex items-center px-3 py-1.5 border border-red-300 shadow-sm text-xs font-medium rounded-md text-red-700 bg-white hover:bg-red-50"
                            >
                              Remove
                            </button>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="text-center">
                        <svg
                          className="mx-auto h-12 w-12 text-gray-400"
                          stroke="currentColor"
                          fill="none"
                          viewBox="0 0 48 48"
                          aria-hidden="true"
                        >
                          <path
                            d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                            strokeWidth={2}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                        <Label htmlFor="presenterPhoto" className={isFormLocked ? 'cursor-not-allowed' : 'cursor-pointer'}>
                          <Input
                            id="presenterPhoto"
                            type="file"
                            accept="image/*"
                            onChange={handleImageSelect}
                            disabled={isFormLocked}
                            className="hidden"
                          />
                          <p className={`mt-2 text-sm font-medium ${isFormLocked ? 'text-gray-400 cursor-not-allowed' : 'text-gray-900 cursor-pointer hover:text-gray-700'}`}>
                            Click to upload & crop presenter photo
                          </p>
                          <p className="mt-1 text-xs text-gray-500">
                            You can crop the image to fit your needs
                          </p>
                        </Label>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>


            {/* Upload Progress */}
            {loading && uploadProgress > 0 && (
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Uploading files...</span>
                  <span className="font-medium">{uploadProgress}%</span>
                </div>
                <Progress value={uploadProgress} className="h-2" />
              </div>
            )}
          </div>
        )}

        <DialogFooter className="flex-shrink-0 border-t p-6 bg-background shadow-[0_-4px_6px_-1px_rgb(0_0_0_/_0.05)]">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={loading}
          >
            {isFormLocked ? 'Close' : 'Cancel'}
          </Button>
          {/* Hide submit button when form is locked */}
          {!isFormLocked && (
            <Button
              onClick={handleSubmit}
              disabled={loading || !areRequiredFieldsFilled()}
              className={`${!areRequiredFieldsFilled() && !loading ? 'bg-gray-400 cursor-not-allowed hover:bg-gray-400' : 'bg-purple-600 hover:bg-purple-700'}`}
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Submitting...
                </>
              ) : existingFeedback ? (
                <>
                  <Upload className="h-4 w-4" />
                  Update Submission
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4" />
                  Submit
                </>
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>

    {/* Mixed Data Confirmation Dialog */}
    <Dialog open={showMixedDataConfirmation} onOpenChange={setShowMixedDataConfirmation}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="text-xl flex items-center gap-2">
            <AlertCircle className="h-6 w-6 text-amber-600" />
            Confirm Presenter Details
          </DialogTitle>
        </DialogHeader>

        <div className="py-4">
          <p className="text-gray-700 mb-4">
            We noticed that the phone or email you entered already exists in our system.
          </p>
          <p className="text-gray-700">
            Please enter the Presenter's contact details, or confirm if you are the one presenting the demo at IFTA.
          </p>
        </div>

        <DialogFooter className="flex gap-3">
          <Button
            variant="outline"
            onClick={handleConfirmEdit}
            className="flex-1"
          >
            Edit Details
          </Button>
          <Button
            onClick={handleConfirmYes}
            className="flex-1 bg-purple-600 hover:bg-purple-700"
          >
            Yes, I'm Presenting
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    {/* Image Crop Modal */}
    <ImageCropModal
      open={cropModalOpen}
      onClose={() => setCropModalOpen(false)}
      imageSrc={tempImageSrc}
      onCropComplete={handleCropComplete}
      aspectRatio={1}
      outputSize={{ width: 1000, height: 1000 }}
      title="Crop Presenter Photo"
    />
    </>
  )
}